import * as React from "react";
import { Header, Grid, Container } from "semantic-ui-react";
import moment = require("moment");

import { medicalTypes as defaultMedicalTypes, MedicalCategory, CustomMedicalType } from "../models";

import { CertificateModel, CertificateMedicalOutcomeModel } from "./models";
import { EmployeeDetails } from "./EmployeeDetails";
import { CustomerDetails } from "./CustomerDetails";
import { ConsultationDetails } from "./ConsultationDetails";
import { OutcomeDetails } from "./OutcomeDetails";
import { ClinicianDetails } from "./ClinicianDetails";
import { RecallDetails } from "./RecallDetails";
import { PpqReviewNotes } from "./PpqReviewNotes";

interface Props {
    model: CertificateModel;
    customMedicalTypes: CustomMedicalType[] | undefined;
}

const HealthSurveillanceCertificate: React.FunctionComponent<Props> = (props) => {
    const { customMedicalTypes } = props;

    const mergedMedicalTypes = React.useMemo(() => {
        if (customMedicalTypes !== undefined) {
            return [...defaultMedicalTypes, ...customMedicalTypes];
        }

        return defaultMedicalTypes;
    }, [customMedicalTypes]);

    const getOutcomesOfCategory = (outcomes: CertificateMedicalOutcomeModel[], category: MedicalCategory) =>
        outcomes.filter(o => mergedMedicalTypes.find(m => m.id === o.medicalTypeId)?.category === category);

    const healthSurveillanceOutcomes = getOutcomesOfCategory(props.model.outcomes, "HealthSurveillance");
    const fitForWorkOutcomes = getOutcomesOfCategory(props.model.outcomes, "FitForWork");

    return (
        <Container className="health-surveillance-certificate">
            <Grid>
                <Grid.Row>
                    <Grid.Column width="16">
                        <Header as="h5" textAlign="right">Customer: {props.model.customerName}</Header>
                        <Header as="h5" textAlign="right" >Date report printed: {moment().format("Do MMMM YYYY")}</Header>
                        <Header as="h1">Health Surveillance &amp; Fitness to Work Outcome Report</Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width="10">
                        <EmployeeDetails {...props.model.employee} />
                    </Grid.Column>
                    <Grid.Column width="6">
                        <CustomerDetails customerName={props.model.customerName} orgStructure={props.model.employee.orgStructure} />
                        { props.model.reviewedBy &&
                            <ClinicianDetails {...props.model.reviewedBy} />
                        }
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row className="consultation-details">
                    <Grid.Column width="10">
                        <ConsultationDetails consultationDate={props.model.consultationDate} reviewDate={props.model.reviewDate} />
                    </Grid.Column>
                </Grid.Row>
                {healthSurveillanceOutcomes.length > 0 &&
                    <>
                        <Grid.Row>
                            <Grid.Column><Header as="h2">Health Surveillance</Header></Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                {healthSurveillanceOutcomes.map(o => (
                                    <OutcomeDetails key={o.medicalTypeId} model={o} customMedicalTypes={props.customMedicalTypes} />
                                ))}
                            </Grid.Column>
                        </Grid.Row>
                    </>
                }
                {fitForWorkOutcomes.length > 0 &&
                    <>
                        <Grid.Row>
                            <Grid.Column><Header as="h2">Fitness for Work</Header></Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                {fitForWorkOutcomes.map(o => (
                                    <OutcomeDetails key={o.medicalTypeId} model={o} customMedicalTypes={props.customMedicalTypes} />
                                ))}
                            </Grid.Column>
                        </Grid.Row>
                    </>
                }
                <Grid.Row>
                    <Grid.Column>
                        <RecallDetails recalls={props.model.recalls} customMedicalTypes={props.customMedicalTypes} />
                    </Grid.Column>
                </Grid.Row>
                {props.model.ppqDeclarationReviewNotes.length > 0 && (
                    <Grid.Row>
                        <Grid.Column>
                            <PpqReviewNotes notes={props.model.ppqDeclarationReviewNotes} />
                        </Grid.Column>
                    </Grid.Row>
                )}
                <Grid.Row>
                    <Grid.Column>
                        <p>
                            Please note that any advice given, and suggested adjustments contained within this report are purely recommendations.
                            It is ultimately a management decision as to whether these can be accommodated.
                        </p>
                        <p>
                            If you would like to discuss this report or require further information or advice, please do not hesitate to contact
                            us: {props.model.customerEmailAddress} or call {props.model.customerPhoneNumber}.
                        </p>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </Container>
    );
};

export { HealthSurveillanceCertificate };
